/* eslint-disable react-hooks/exhaustive-deps */
import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addDirectorFeedback,
  fetchDirectorFeedbacks,
  updateDirectorFeedback,
} from "services/directorFeedbackService";
import {
  DirectorFeedback,
  DirectorFeedbackType,
  FILE_URL,
  Roles,
} from "types";
import { DateView, errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  // app_label.model from FEEDBACK_MODEL.
  model: string;
  objectId?: string | number;
  // The role tag attached to replies (e.g. "prosecutor", "registrar").
  role?: string;
  // When rendered inside the drawer, drop the outer card + header chrome.
  embedded?: boolean;
};

const typeBadge = (f: DirectorFeedback) => {
  if (f?.is_directive || f?.feedback_type === "directive")
    return "bg-red-50 text-red-700 border-red-200";
  if (f?.feedback_type === "feedback")
    return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
};

const DirectorNotesPanel: FC<Props> = ({
  model,
  objectId,
  role,
  embedded,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const isDirector = hasRole(loginData?.roles, [
    Roles.director,
    Roles.economicDirector,
    Roles.corruptionDirector,
    Roles.variousDirector,
  ]);

  const [items, setItems] = useState<DirectorFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState<DirectorFeedbackType>("comment");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState<Record<string, string>>({});

  const baseUrl = `${process.env.REACT_APP_API_BASE_URL}${FILE_URL.DIRECTOR_FEEDBACK}`;

  const load = () => {
    if (!objectId) return;
    setLoading(true);
    fetchDirectorFeedbacks({
      model,
      object_id: objectId,
      include_replies: true,
    })
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [model, objectId]);

  const handleCreate = () => {
    if (!message.trim()) {
      showTooltip(
        t("message_required", { defaultValue: "Message is required" }),
        "error",
      );
      return;
    }
    setSubmitting(true);
    const isDirective = type === "directive";
    addDirectorFeedback({
      model,
      object_id: objectId,
      feedback_type: type,
      is_directive: isDirective,
      requires_action: isDirective,
      message,
      author_name: loginData?.full_name || "",
      author_id: String(loginData?.user_id || ""),
      admin_unit_id: loginData?.admin_unit
        ? String(loginData.admin_unit)
        : null,
      role,
      file,
    })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setMessage("");
        setFile(null);
        setType("comment");
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      )
      .finally(() => setSubmitting(false));
  };

  const handleReply = (parent: DirectorFeedback) => {
    const text = replyText[parent.id || ""] || "";
    if (!text.trim()) {
      showTooltip(
        t("message_required", { defaultValue: "Message is required" }),
        "error",
      );
      return;
    }
    addDirectorFeedback({
      parent: parent.id,
      feedback_type: "feedback",
      message: text,
      author_name: loginData?.full_name || "",
      author_id: String(loginData?.user_id || ""),
      role,
    })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setReplyText((p) => ({ ...p, [parent.id || ""]: "" }));
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  const setStatus = (item: DirectorFeedback, status: string) => {
    if (!item.id) return;
    updateDirectorFeedback(item.id, { status })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  const renderItem = (item: DirectorFeedback, depth = 0) => {
    const isDirective = item?.is_directive || item?.feedback_type === "directive";
    const open = isDirective && item?.status !== "resolved";
    return (
      <div
        key={item.id}
        className={`rounded-xl border ${
          isDirective ? "border-red-200 bg-red-50/40" : "border-gray-200 bg-white"
        } ${depth > 0 ? "ml-5" : ""}`}
      >
        <div className="p-3.5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 shrink-0 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold">
                {(item?.author_name || "?")
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((p) => p[0])
                  .join("")
                  .toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {item?.author_name || t("n_a")}
                  {item?.role ? (
                    <span className="ml-1 text-xs font-normal text-gray-400">
                      · {item.role}
                    </span>
                  ) : null}
                </p>
                {item?.created_at && (
                  <p className="text-[11px] text-gray-400">
                    {DateView(item.created_at)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {item?.requires_action && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  {t("requires_action", { defaultValue: "Requires Action" })}
                </span>
              )}
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${typeBadge(
                  item,
                )}`}
              >
                {t(`feedback_type_${item?.feedback_type}`, {
                  defaultValue:
                    item?.feedback_type === "directive"
                      ? "Directive"
                      : item?.feedback_type === "feedback"
                        ? "Feedback"
                        : "Comment",
                })}
              </span>
              {item?.status && item?.status !== "open" && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {item.status}
                </span>
              )}
            </div>
          </div>

          <p className="mt-2 text-sm text-gray-800 whitespace-pre-line">
            {item?.message}
          </p>

          {Array.isArray(item?.documents) && item.documents.length > 0 && (
            <div className="mt-2">
              <DocumentViewerModal
                documents={item.documents as any}
                baseUrl={baseUrl}
                layout="list"
                hideEmptyFile
              />
            </div>
          )}

          {/* Acknowledge / Resolve on directives (recipient side). */}
          {isDirective &&
            !isDirector &&
            item?.status !== "resolved" &&
            depth === 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setStatus(item, "acknowledged")}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {t("acknowledge", { defaultValue: "Acknowledge" })}
                </button>
                <button
                  type="button"
                  onClick={() => setStatus(item, "resolved")}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  {t("resolve", { defaultValue: "Resolve" })}
                </button>
              </div>
            )}

          {/* Reply box (recipients respond to a note / directive). */}
          {depth === 0 && open && (
            <div className="mt-3 flex items-end gap-2">
              <textarea
                value={replyText[item.id || ""] || ""}
                onChange={(e) =>
                  setReplyText((p) => ({ ...p, [item.id || ""]: e.target.value }))
                }
                rows={1}
                placeholder={t("write_a_reply", {
                  defaultValue: "Write a reply...",
                })}
                className="flex-1 bg-white border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring focus:ring-red-200"
              />
              <button
                type="button"
                onClick={() => handleReply(item)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                {t("reply", { defaultValue: "Reply" })}
              </button>
            </div>
          )}
        </div>

        {/* Nested replies */}
        {Array.isArray(item?.replies) && item.replies.length > 0 && (
          <div className="px-3.5 pb-3.5 space-y-2">
            {item.replies.map((r) => renderItem(r, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const body = (
    <div className={embedded ? "space-y-4" : "p-5 space-y-4"}>
        {/* Composer (director only) */}
        {isDirector && (
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {(["comment", "feedback", "directive"] as DirectorFeedbackType[]).map(
                (tp) => (
                  <button
                    key={tp}
                    type="button"
                    onClick={() => setType(tp)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                      type === tp
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {t(`feedback_type_${tp}`, {
                      defaultValue:
                        tp === "directive"
                          ? "Directive"
                          : tp === "feedback"
                            ? "Feedback"
                            : "Comment",
                    })}
                  </button>
                ),
              )}
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder={t("write_a_note", {
                defaultValue: "Write a note...",
              })}
              className="w-full bg-white border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring focus:ring-red-200"
            />
            <FileUpload
              label={t("attachment", { defaultValue: "Attachment (optional)" })}
              fileTypes="*"
              required={false}
              onChange={setFile}
            />
            <div className="flex justify-end">
              <button
                type="button"
                disabled={submitting}
                onClick={handleCreate}
                className="px-5 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {submitting
                  ? t("loading", { defaultValue: "Loading..." })
                  : t("post_note", { defaultValue: "Post Note" })}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            {t("loading", { defaultValue: "Loading..." })}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm bg-gray-50 rounded-lg">
            {t("no_director_notes", { defaultValue: "No notes yet" })}
          </div>
        ) : (
          <div className="space-y-3">
            {[...items]
              .filter((i) => !i.parent)
              .sort(
                (a, b) =>
                  new Date(b?.created_at || "").getTime() -
                  new Date(a?.created_at || "").getTime(),
              )
              .map((item) => renderItem(item))}
          </div>
        )}
    </div>
  );

  if (embedded) return body;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
        <span className="text-base">🗒️</span>
        <h3 className="text-base font-semibold text-gray-900">
          {t("director_notes", { defaultValue: "Director Notes" })}
        </h3>
      </div>
      {body}
    </div>
  );
};

export default DirectorNotesPanel;
